/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable, NgZone } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable, BehaviorSubject, Subject, Subscription, combineLatest } from 'rxjs';
import { delay, first, filter, takeUntil, map, pairwise, distinctUntilChanged } from 'rxjs/operators';

import { clipString, getNested } from 'utils/basic-utils';
declare const fancyNotification: any;

import { renderQueue } from 'src/app/plain-javascript/services/RenderQueue';
import {
	dateCache,
	Ticket,
	Side,
	ProjectMember,
	TicketType,
	PULL_TICKET_WIDTH_SIZES,
	ListLike,
} from '@touchplan/common';

import { InteractionBlocker } from 'src/app/plain-javascript/services/interaction-blocker';

import { CoreState } from 'src/app/app-data/interfaces/app-state.interface';
import { CalendarWorkdays, generateLocalExceptionDays } from '@touchplan/ticket';
import { Dependencies } from 'src/app/app-data/models/Dependencies';
import { Swimlanes } from 'src/app/app-data/state/Swimlanes';
import { DefaultActionTypes } from 'src/app/common/fancy-firebase/generics/DefaultActionTypes';

import { CanvasConnector } from 'src/app/app-data/models/CanvasConnector';
import { DebugRectangles } from 'src/app/app-data/models/DebugRectangles';

import {
	VarianceList,
	PriorityList,
	TicketExceptionDaysList,
} from 'src/app/common/fancy-firebase/services/factories';
import { TicketList } from '../common/fancy-firebase-ticket/lists/TicketList';
import { TimelineTicketList } from '../common/fancy-firebase-ticket/lists/TimelineTicketList';
import { ConstraintList } from '../common/fancy-firebase-ticket/lists/ConstraintList';
import { PlanObject } from 'src/app/common/fancy-firebase/objects/PlanObject';
import {
	FancyActionTypes,
	FancyTicketAction,
	TicketActionTypes,
	TicketDuplicatedAction,
	TicketFocusedAction,
} from 'src/app/app-data/actions/ticket-actions';

import { applyScopedActions, AllThePlanActions } from 'src/app/app-data/actions/plan-actions';

import { TicketDragService } from 'src/app/app-data/models/TicketDragService';
import { SwimlaneDragService } from 'src/app/app-data/models/SwimlaneDragService';

import {
	ActiveColumnService,
	DatePositionValidator,
} from 'src/app/services/legacy-helpers/ajs-upgrade-providers';

import { DataInfusedTicketCopy } from './DataInfusedTicketCopy'; // DEPRECATED // just load it in for debugging
import { LaborCounts } from 'src/app/app-data/state/LaborCounts';

import { ProjectObject } from 'src/app/common/fancy-firebase/objects/ProjectObject';
import { FbdbSettingsObject } from 'src/app/common/fancy-firebase/objects/FbdbSettingsObject';
import { EnabledChecker, IEnabledFeatures } from 'src/app/app-data/models/EnabledChecker';
import { CustomFieldListType, CustomFieldsApi } from '../app-data/state/CustomFieldsApi';
import { RolesApi } from '../app-data/state/RolesApi';
import { FeatureFlagManager } from './FeatureFlagManager';
import { DynamicFlagTypes } from '@touchplan/feature-flags';
import { PlansApi } from '../app-data/state/PlansApi';
import { analyticsEventsService } from 'src/app/plain-javascript/services/analytics/AnalyticsEventsService';
import { fancyFirebaseGetters, fancyFirebaseReferencer } from '@touchplan/fancy-referencer';
import { FileAttachCloneService } from '../modules/file-attach/file-attach-clone.service';
import { NORMAL } from '@touchplan/strings';
import { extractHostName } from '../plain-javascript/allow-list/allow-list';
import { ColorsApi } from '../app-data/state/ColorsApi';
import { InvitedUsersApi } from '../app-data/state/InvitedUsersApi';
import { LocationsApi } from '../app-data/state/LocationsApi';
import { ProjectMembersApi } from '../app-data/state/ProjectMembersApi';
import { LoggerServiceFactory } from '../common/logging/LoggerServiceFactory';
import { ILoggerService } from '@touchplan/logging';
import { UserPreferenceStateService } from './user-preference-state.service';

import firebase from 'firebase/compat/app';
import { GranularEnabledService } from '../common/granular-time/granular-enabled.service';

export enum StateEvents {
	'INVALID' = -2, //not actually a valid state, only exists for error handling
	'BOOMED' = -1, //blow up
	'UNINITIALIZED' = 0, //default
	'INITIALIZED' = 1, //setup called, angular stuff loaded
	'SCOPE_SOUPED' = 2, //angularjs plan_controller initialized
	'CANVAS_LOADED' = 3, //rendererApplication setup
	'FULLY_LOADED' = 100, //baked potato
}

@Injectable()
export class PlanState implements CoreState {
	public get editProbablyOpen() {
		if (!this.tickets || !this.floatingTickets) {
			return false;
		}
		return this.tickets.editTicket$.getValue().size || this.floatingTickets.editTicket$.getValue().size;
	}

	constructor(
		public activeColumnService: ActiveColumnService,
		private datePositionValidator: DatePositionValidator,
		public angularZone: NgZone,
		public fileAttachCloneService: FileAttachCloneService,
		private featureFlagManager: FeatureFlagManager,
		private userPreferenceStateService: UserPreferenceStateService,
		private granularEnabledService: GranularEnabledService
	) {
		this.logger = LoggerServiceFactory.create('PlanState');
		this.stateChange$.subscribe((state) => (this.stateSync = state));

		applyScopedActions(this.actions, this);
	}
	private stateLifeCycle?: PlanStateLifeCycle;
	private internalShutdown = new Subject();
	private unsubscribeList: Subscription[];
	private logger: ILoggerService;

	public route: ActivatedRoute;
	/** a version of the renderQueue that completes when the planState is destroyed */
	public renderQueueWithCleanup$: Observable<number>;

	/** extremely temporary thing for the meeting popup. Remove this and solve better in r37 */
	public showMeetingPopup = false;

	public tickets: TicketList;
	public timelineTickets: TimelineTicketList;
	public constraints: ConstraintList;
	public floatingTickets: TicketList;
	public rolesApi: RolesApi;
	public locationsApi: LocationsApi;
	public variances: VarianceList;
	public plansApi: PlansApi;
	public projectMembersApi: ProjectMembersApi;
	public invitedUsersApi: InvitedUsersApi;
	public priorities: PriorityList;
	public plan: PlanObject;
	public calendarWorkdays: CalendarWorkdays;
	public authedProjectMember$: Observable<ProjectMember>;
	public authedProjectMemberSync: ProjectMember;
	public dependencies: Dependencies;
	public swimlanes: Swimlanes;
	public laborCounts: LaborCounts;
	public projectLaborCounts: LaborCounts;
	public ticketExceptionDaysList: TicketExceptionDaysList;
	public fbdbSettings: FbdbSettingsObject;
	public project: ProjectObject;
	public wideTicketSize$: BehaviorSubject<number | null>;
	public roleColorsApi: ColorsApi;
	public locationColorsApi: ColorsApi;
	public toggleUndoPanel: () => void;

	public cycleTicket$ = new Subject();
	public allowedProjectLinks$ = new BehaviorSubject<string[]>(null);
	public filterTransparencyValue$: BehaviorSubject<number>;

	/** probably don't use this */
	public legacySearch: {
		in$: BehaviorSubject<Ticket[]>;
		out$: Observable<Ticket[]>;
	};

	public planId?: string;
	public projectId?: string;

	/** legacy access to the old plan_controller scope. Seriously think about what you're doing if you decide to use this. */
	public scopeSoup: any;

	public stateChange$ = new BehaviorSubject<StateEvents>(StateEvents.UNINITIALIZED); //maybe hide the subject and expose an .asObservable()
	public stateSync = StateEvents.UNINITIALIZED;

	public actions: AllThePlanActions = <AllThePlanActions>{}; //shh... don't question it
	public canvasConnector?: CanvasConnector;

	public ticketDragService: TicketDragService;
	public swimlaneDragService: SwimlaneDragService;

	public debugRectangles: DebugRectangles = new DebugRectangles();

	/** @deprecated This may still be used by CS, but the Clipboard UI is replacing it... */
	public ticketCopy: DataInfusedTicketCopy;

	public enabledChecker: EnabledChecker;
	public interactionBlocker: InteractionBlocker;

	public customFieldsApi: CustomFieldsApi;

	//DEVTEXT - this should be better orginized, not yet sure exactly how so
	private _shakeVariancePrompt = new Subject<void>();
	public shakeVariancePrompt$ = this._shakeVariancePrompt.asObservable();

	//some temporary interfaces from the app code to old app functionality
	public legacyActions = {
		isConstraintSidePanelClean: true,
		clearFloatingTickets: () => {
			const floatingTickets = this.floatingTickets;
			if (floatingTickets.selectedTicket$.getValue().list.size) {
				this.actions.unSelectTicket(floatingTickets.selectedTicket$.getValue().list, floatingTickets);
				this.scopeSoup.floatingTickets.selection.empty(true);
			}
		},
		getOldTicketsFromNew: (newTickets: ListLike<Ticket>, legacyTicketList?: any) => {
			const arr: any[] = [];
			if (!legacyTicketList) {
				legacyTicketList = this.scopeSoup.tickets;
			}
			//using forEach instead of [...] because it works the same for arrays and maps
			newTickets.forEach((t) => {
				const oldT = legacyTicketList.$getRecord(t.$id);
				if (oldT) {
					arr.push(oldT);
				} else {
					this.logger.log("old and new tickets aren't synced?", t.$id);
				}
			});
			return arr;
		},
		getNewTicketsFromOld: (oldTickets: ListLike<any>, ticketList?: TicketList) => {
			if (!ticketList) {
				ticketList = this.tickets;
			}
			const arr: Ticket[] = [];
			//using forEach instead of [...] because it works the same for arrays and maps
			oldTickets.forEach((t) => {
				const newT = ticketList.syncList.get(t.$id);
				if (newT) {
					arr.push(newT);
				} else {
					this.logger.log("old and new tickets aren't synced?", t.$id);
				}
			});
			return arr;
		},
		//DEVTEXT - Seems like doing this as a legacy function isn't really going to work due
		// to cases where the timeline data may stick around. This is because the new
		// code is more local, the old code is more "in sync"
		toggleTimelineTicket: (planId: string, ticketId: string, force?: boolean, disableAddText?: boolean) => {
			const $scope = this.scopeSoup;

			const legacyRecord = $scope.timelineTickets.$getCompositeRecord(planId, ticketId);
			const modernRecord =
				this.tickets.syncList.get(ticketId) || this.tickets.syncList.get(planId + ',' + ticketId);
			const turnOn = force !== undefined ? force : !legacyRecord;

			const modernName = clipString(getNested(modernRecord, 'view.activityName') || 'No Description');
			const modernFinish = getNested(modernRecord, 'view.plannedFinish');

			if (!turnOn) {
				$scope.timelineTickets.$remove(legacyRecord);
			} else {
				$scope.timelineTickets.addIndex(planId, ticketId);
				let message = '"' + modernName + '" added to the timeline in all plans.';
				if (!modernFinish) {
					message += ' (Will appear once a date has been calculated.)';
				}
				if (!disableAddText) {
					fancyNotification.simpleMessage(message, $scope);
				}
			}

			return turnOn;
		},
		openConstraintNotePanel: (constraintId: string) => {
			const $scope = this.scopeSoup;
			$scope.setSidebar('constraints');
			/**
			 * For the brave souls who get this far: You are the chosen ones,
			 * the valiant knights of programming who toil away, without rest,
			 * fixing our most awful code. To you, true saviors, kings of men,
			 * I say this: never gonna give you up, never gonna let you down,
			 * never gonna run around and desert you. Never gonna make you cry,
			 * never gonna say goodbye. Never gonna tell a lie and hurt you.
			 *
			 * So we do this hacky thing because:
			 * 1.) tryOpenDetails is only created when the Constraint Side panel is opened for the first time
			 * 2.) The delay is because the tryOpenDetails is not created immediately when the sidebar is open...so we delay
			 * 3.) 300 is an arbitrary number that works.
			 */
			const ob$: BehaviorSubject<any> = new BehaviorSubject(null);
			ob$.pipe(first(), delay(300)).subscribe(() => {
				$scope.constraintSidePanelApi.tryOpenDetails(constraintId);
			});
		},
		// MOC-4046
		closeConstraintPanel: () => {
			const $scope = this.scopeSoup;
			$scope.closeSidebar();
		},
		markConstraintSidePanelClean: () => {
			this.legacyActions.isConstraintSidePanelClean = true;
		},
		markConstraintSidePanelDirty: () => {
			this.legacyActions.isConstraintSidePanelClean = false;
		},
	};
	public makeVariancePromptShake() {
		this._shakeVariancePrompt.next();
	}

	public setup(
		route: ActivatedRoute,
		canvasConnector: CanvasConnector,
		tickets: TicketList,
		timelineTickets: TimelineTicketList,
		constraints: ConstraintList,
		floatingTickets: TicketList,
		rolesApi: RolesApi,
		locationsApi: LocationsApi,
		variances: VarianceList,
		plansApi: PlansApi,
		projectMembersApi: ProjectMembersApi,
		invitedUsersApi: InvitedUsersApi,
		priorities: PriorityList,
		plan: PlanObject,
		calendarWorkdays: CalendarWorkdays,
		authedProjectMember$: Observable<ProjectMember>,
		dependencies: Dependencies,
		swimlanes: Swimlanes,
		laborCount: LaborCounts,
		projectLaborCount: LaborCounts,
		ticketExceptionDaysList: TicketExceptionDaysList,
		fbdbSettings: FbdbSettingsObject,
		project: ProjectObject,
		customFieldsApi: CustomFieldsApi,
		roleColorsApi: ColorsApi,
		locationColorsApi: ColorsApi
	) {
		this.unsubscribeList = [];
		this.stateLifeCycle = new PlanStateLifeCycle();
		this.route = route;
		this.canvasConnector = canvasConnector;
		this.tickets = tickets;
		this.timelineTickets = timelineTickets;
		this.constraints = constraints;
		this.floatingTickets = floatingTickets;
		this.rolesApi = rolesApi;
		this.locationsApi = locationsApi;
		this.variances = variances;
		this.plansApi = plansApi;
		this.projectMembersApi = projectMembersApi;
		this.invitedUsersApi = invitedUsersApi;
		this.priorities = priorities;
		this.plan = plan;
		this.calendarWorkdays = calendarWorkdays;
		this.filterTransparencyValue$ = new BehaviorSubject(null);

		this.authedProjectMember$ = authedProjectMember$;
		this.authedProjectMember$
			.pipe(takeUntil(this.internalShutdown))
			.subscribe((authedPM) => (this.authedProjectMemberSync = authedPM));

		this.dependencies = dependencies;
		this.swimlanes = swimlanes;
		this.laborCounts = laborCount;
		this.projectLaborCounts = projectLaborCount;
		this.ticketExceptionDaysList = ticketExceptionDaysList;
		this.fbdbSettings = fbdbSettings;
		this.project = project;
		this.customFieldsApi = customFieldsApi;
		this.roleColorsApi = roleColorsApi;
		this.locationColorsApi = locationColorsApi;

		this.ticketDragService = new TicketDragService(this, this.featureFlagManager, {
			activeColumnService: this.activeColumnService,
			datePositionValidator: this.datePositionValidator,
			granularEnabledService: this.granularEnabledService,
		});
		this.swimlaneDragService = new SwimlaneDragService(this, {
			activeColumnService: this.activeColumnService,
			datePositionValidator: this.datePositionValidator,
		});

		this.wideTicketSize$ = new BehaviorSubject<number | null>(PULL_TICKET_WIDTH_SIZES[NORMAL]);

		window.planState = this;

		this.renderQueueWithCleanup$ = renderQueue.ob$.pipe(takeUntil(this.internalShutdown));

		this.projectId = this.route.snapshot.paramMap.get('projectId');
		this.planId = this.route.snapshot.paramMap.get('planId');

		const newState = this.stateLifeCycle.angularLoaded();
		if (newState !== StateEvents.INVALID) {
			this.stateChange$.next(newState);
		}

		this.ticketCopy = new DataInfusedTicketCopy(this); // DEPRECATED

		this.ticketEventHandling();
		this.ticketEventHandlingForAnalytics();

		this.swimlaneEventHandling();

		this.fetchAllowedLinks();

		this.legacySearch = {} as any;
		this.legacySearch.in$ = new BehaviorSubject([]);
		this.legacySearch.out$ = this.legacySearch.in$.pipe(
			takeUntil(this.internalShutdown),
			map((legacyList) => {
				const list: Ticket[] = [];
				const syncTicketList = this.tickets.syncList;
				legacyList.forEach((oldTicket) => {
					const ticket = syncTicketList.get(oldTicket.$id);
					if (ticket) {
						list.push(ticket);
					}
				});
				return list;
			})
		);

		this.interactionBlocker = new InteractionBlocker(this);

		this.enabledChecker = new EnabledChecker(
			this.project.object$,
			this.fbdbSettings.object$,
			this.plan.object$
		);
		// BEGIN PLACEHOLDER REPLACE AS PART OF TP-6657
		// fix this by nuking it and folding anything remaining in the the prefs service
		// MOC-3188 - Combine our enabled features data with the session storage data. That way, we only have to subscribe to session storage
		// to get a full list of features enabled at either the project of global fbdb level
		this.enabledChecker.changed$
			.pipe(takeUntil(this.internalShutdown))
			.subscribe((enabledFeatures: IEnabledFeatures) => {
				for (const key in enabledFeatures) {
					//this is currently broken, userPrefs doesn't support everything in enabledFeatures
					(this.userPreferenceStateService as any)[key] = enabledFeatures[key as keyof IEnabledFeatures];
				}
			});
		// END PLACEHOLDER REPLACE AS PART OF TP-6657
		const wideTicketFlagAndPlan = combineLatest([
			this.plan.object$,
			this.featureFlagManager.getFlag$(DynamicFlagTypes.WIDE_PULL_TICKET),
		]);
		wideTicketFlagAndPlan
			.pipe(
				filter(([planObj, flag]) => !!planObj && !!planObj.view),
				map(([planObj, flag]) =>
					flag &&
					planObj.view.pullTicketWidth &&
					planObj.view.pullTicketWidth >= PULL_TICKET_WIDTH_SIZES[NORMAL]
						? planObj.view.pullTicketWidth
						: null
				),
				distinctUntilChanged(),
				takeUntil(this.internalShutdown)
			)
			.subscribe(this.wideTicketSize$);
	}

	public boom() {
		//this can potentially be called more than once
		if (this.stateSync === StateEvents.BOOMED) {
			return;
		}

		// Logging.warning("ʘ‿ʘ : The memory, she leaks");
		// Logging.warning("щ（ﾟДﾟщ）: Da vram, she bleeds");
		// TODO Don't do this FFS YOU MONSTER ლ(｀ー´ლ)
		//(<any>window).TouchPlanCanvas.destroy(true);
		this.internalShutdown.next(true);

		this.stateChange$.next(StateEvents.BOOMED);

		this.tickets.destroy();
		this.tickets = null;
		this.timelineTickets.destroy();
		this.timelineTickets = null;
		this.constraints.destroy();
		this.constraints = null;
		this.floatingTickets.destroy();
		this.floatingTickets = null;
		this.rolesApi.destroy();
		this.rolesApi = null;
		this.locationsApi.destroy();
		this.locationsApi = null;
		this.variances.destroy();
		this.variances = null;
		this.plansApi.destroy();
		this.plansApi = null;
		this.projectMembersApi.destroy();
		this.projectMembersApi = null;
		this.invitedUsersApi.destroy();
		this.invitedUsersApi = null;
		this.priorities.destroy();
		this.priorities = null;
		this.plan.destroy();
		this.plan = null;
		this.debugRectangles.destroy(); //don't null this
		this.dependencies.destroy();
		this.dependencies = null;
		this.swimlanes.destroy();
		this.swimlanes = null;
		this.laborCounts.destroy();
		this.laborCounts = null;
		this.projectLaborCounts.destroy();
		this.projectLaborCounts = null;
		this.ticketExceptionDaysList.destroy();
		this.ticketExceptionDaysList = null;
		this.roleColorsApi.destroy();
		this.roleColorsApi = null;
		this.locationColorsApi.destroy();
		this.locationColorsApi = null;
		this.scopeSoup = null;
		this.scopeSoup = null;
		this.canvasConnector.destroy();
		this.canvasConnector = null;
		this.calendarWorkdays.destroy();
		this.calendarWorkdays = null;
		this.ticketDragService.destroy();
		this.ticketDragService = null;
		this.swimlaneDragService.destroy();
		this.swimlaneDragService = null;
		this.ticketCopy.destroy();
		this.ticketCopy = null; // DEPRECATED
		this.enabledChecker.destroy();
		this.enabledChecker = null;
		this.wideTicketSize$.complete();
		this.wideTicketSize$ = null;

		this.interactionBlocker.destroy();
		this.interactionBlocker = null;

		this.customFieldsApi.destroy();
		this.customFieldsApi = null;

		this.authedProjectMember$ = null;
		this.authedProjectMemberSync = null;
		this.projectId = null;
		this.planId = null;

		this.unsubscribeList.forEach((sub) => sub.unsubscribe());

		this.userPreferenceStateService.hideCompletedTickets = false;

		this.filterTransparencyValue$.complete();
		this.filterTransparencyValue$ = null;

		delete window.planState;
	}

	public assignScopeSoup(soup: any) {
		const newState = this.stateLifeCycle.scopeSoupLoaded();
		if (newState !== StateEvents.INVALID) {
			this.scopeSoup = soup;
			this.stateChange$.next(newState);
		}
	}
	//TODO fix the typing here, the dependency tree makes it a bad idea to import AbstractApplication
	public assignCanvasConnector(application: any) {
		const newState = this.stateLifeCycle.canvasLoaded();
		if (newState !== StateEvents.INVALID) {
			this.canvasConnector.connectApplication(application);
			this.stateChange$.next(newState);
		}
	}

	//TODO - move somewhere else
	// attempt handling specific ticket actions to run some additional code based on it
	private ticketEventHandling() {
		const stateSnapshot = this.route.snapshot.paramMap;

		//duplicated filtering (from before) is just useless
		this.tickets.rawEvent$.pipe(takeUntil(this.internalShutdown)).subscribe((action) => {
			const isConstraint = action.payload.view.type === TicketType.Constraint;
			const ticketRemovedFromPlan =
				action.type === TicketActionTypes.ticketSavedToFloating ||
				action.type === TicketActionTypes.deleteTicket ||
				action.type === TicketActionTypes.internalDeleteTicket;
			// let ticketAddedToPlan = action.type === TicketActionTypes.floatingTicketSaved || action.type === TicketActionTypes.createTicketWithId;
			const ticketAddedToPlan = this.tickets.thisClientAddedAction(action.type);
			const ticketPersistData = action.type === TicketActionTypes.ticketPersistData;
			const ticketDuplicated = action.type === TicketActionTypes.ticketDuplicated;
			const ticketReturnedToDefault = action.type === TicketActionTypes.ticketChangedToDefault;

			//constraint removed
			if (isConstraint && ticketRemovedFromPlan) {
				const constraint = this.scopeSoup.constraints2.$getByTicketId(action.key);
				this.scopeSoup.constraints2.$remove(constraint);
			}
			//constraint created (in plan)
			if (isConstraint && ticketAddedToPlan) {
				this.scopeSoup.constraints2.addIndex(stateSnapshot.get('planId'), action.key);
			}
			if (ticketRemovedFromPlan) {
				// timeline ticket removed
				const timelineRec = this.scopeSoup.timelineTickets.$getCompositeRecord(
					stateSnapshot.get('planId'),
					action.key
				);
				if (timelineRec) {
					this.scopeSoup.timelineTickets.$remove(timelineRec);
				}

				// remove corresponding custom field ticket values if any exists
				// this behavior should be removed when we no long delete tickets
				this.customFieldsApi.removeCustomFieldTicketValue(action.key);

				// remove file attach & its refs
				if (this.featureFlagManager.getFlag(DynamicFlagTypes.FILE_ATTACH_ENABLED)) {
					this.fileAttachCloneService.deleteTicketAttachments(
						this.projectId,
						this.planId,
						action.payload.$id
					);
				}
			}

			//added to timeline and didn't save (temp workaround)
			if (ticketReturnedToDefault && action.payload.wasJustAddedToTimeline !== undefined) {
				const ticketId = action.payload.$id;
				const planId = this.planId;
				//this is monitoring planState.tickets and thus can never be true
				// if (action.payload.isTimelineTicket) {
				// 	planId = action.payload.timelineTicketSplitId[0];
				// 	ticketId = action.payload.timelineTicketSplitId[1];
				// }
				this.legacyActions.toggleTimelineTicket(
					planId,
					ticketId,
					!action.payload.wasJustAddedToTimeline,
					true
				);
			}

			//update exception days delta based on workdays for non-actualized tickets
			// This isActualized check _may_ cause issues when changing exception days and completing in one edit session
			if (
				(ticketPersistData || ticketDuplicated) &&
				!action.payload.database.isActualized &&
				action.payload.database.side === Side.ACTIVE
			) {
				const start = action.payload.database.plannedStart;
				const finish = action.payload.database.plannedFinish;
				const days = generateLocalExceptionDays(start, action.payload.database.workingDays);

				this.logger.log('test exception write');

				const trimmed: { [key: string]: boolean } = {};
				days.forEach((working, key) => {
					// This chopping (the if) is probably not needed because generateLocalExceptionDays won't generate days outside of the start/finish
					if (key >= start && key <= finish) {
						// This is probably the only thing that needs to be done (convert the map to an object that firebase can handle)
						trimmed[key] = working;
					}
				});

				//query calendar workdays and use dateCache add days to iterate the thing, remove that date if it's the same
				const globalWorkdays = this.calendarWorkdays.calculateWorkdays(
					start,
					finish,
					action.payload.database.roleId
				);
				globalWorkdays.forEach((working, idx) => {
					const date = dateCache.addDays(start, idx);
					if (trimmed[date] !== undefined && trimmed[date] === working) {
						trimmed[date] = null;
					}
				});

				if (Object.keys(trimmed).length) {
					this.testExceptionWrite(action.key, trimmed);
				}
			}

			if (ticketDuplicated) {
				this.customFieldsApi.duplicateCustomFieldTicketValues(
					(<TicketDuplicatedAction>action).config.oldTicketId,
					action.payload.$id
				);
			}

			if (action.type === TicketActionTypes.ticketFocused) {
				if (!(<TicketFocusedAction>action).config?.onlyHighlight) {
					this.canvasConnector.camera.focusOnTicket(
						action.payload,
						(<TicketFocusedAction>action).config?.focusOnTicketsOptions
					);
				}
			}
		});

		//monitor floating tickets editTickets for auto-removal MOC-3385
		this.floatingTickets.editTicket$
			.pipe(
				map((list) => [...list.values()]),
				pairwise(),
				map((tuple) => {
					const prev = tuple[0];
					const current = tuple[1];
					const shouldDeleteList: Ticket[] = [];
					prev.forEach((t) => {
						if (t.ticketJustAdded && current.indexOf(t) === -1 && this.floatingTickets.syncList.has(t.$id)) {
							shouldDeleteList.push(t);
						}
					});
					return shouldDeleteList;
				}),
				filter((list) => !!list.length),
				takeUntil(this.internalShutdown)
			)
			.subscribe((ticketsToDelete) => {
				this.actions.deleteTicket(ticketsToDelete, this.floatingTickets);
			});

		this.floatingTickets.rawEvent$.pipe(takeUntil(this.internalShutdown)).subscribe((action) => {
			let oldTickets;
			switch (action.type) {
				case TicketActionTypes.ticketSelected:
					oldTickets = this.legacyActions.getOldTicketsFromNew(
						[action.payload],
						this.scopeSoup.floatingTickets
					);
					if (oldTickets.length) {
						oldTickets[0].$parent.selection.add(oldTickets[0], true);
					}
					break;
				case TicketActionTypes.ticketUnselected:
					oldTickets = this.legacyActions.getOldTicketsFromNew(
						[action.payload],
						this.scopeSoup.floatingTickets
					);
					if (oldTickets.length) {
						oldTickets[0].$parent.selection.remove(oldTickets[0], true);
					}
					break;
				case TicketActionTypes.ticketDuplicated:
					this.customFieldsApi.duplicateCustomFieldTicketValues(
						(<TicketDuplicatedAction>action).config.oldTicketId,
						action.payload.$id,
						CustomFieldListType.OnDeckTicketValue
					);
					break;
				case TicketActionTypes.deleteTicket:
				case TicketActionTypes.internalDeleteTicket:
					// remove corresponding on deck custom field ticket values if any exists
					// this behavior should be removed when we no long delete tickets
					this.customFieldsApi.removeCustomFieldTicketValue(
						action.key,
						CustomFieldListType.OnDeckTicketValue
					);
					break;
				default:
					break;
			}
		});
	}

	//TODO - move somewhere else
	// Handle specific ticket actions for the sake of generating corresponding analytics events
	private ticketEventHandlingForAnalytics() {
		this.tickets.rawEvent$.pipe(takeUntil(this.internalShutdown)).subscribe((action: FancyTicketAction) => {
			this.handlingTicketActionForAnalytics(action, false);
		});

		// Monitor floating tickets as well:
		this.floatingTickets.rawEvent$
			.pipe(takeUntil(this.internalShutdown))
			.subscribe((action: FancyTicketAction) => {
				this.handlingTicketActionForAnalytics(action, true);
			});
	}

	private handlingTicketActionForAnalytics(action: FancyTicketAction, isFloatingTicket: boolean) {
		const ticket: Ticket = action.payload;
		const ticketType = ticket.view.type;
		const ticketId = ticket.$id;

		// Look for kinds of adds, but not "childAdded", since those are FB loads,
		// and we don't want to generate analytics events for those:
		if (this.tickets.isAddAction(action.type) && action.type !== FancyActionTypes.childAdded) {
			if (ticketType === TicketType.Constraint) {
				analyticsEventsService.addedAConstraint(ticketId, this.planId, action.type, isFloatingTicket);
			} else if (ticketType === TicketType.Milestone) {
				analyticsEventsService.addedAMilestone(ticketId, this.planId, action.type, isFloatingTicket);
			} else {
				analyticsEventsService.addedATicket(ticketId, this.planId, action.type, isFloatingTicket);
			}
		} else if (this.tickets.isRemoveAction(action.type) && action.type !== FancyActionTypes.childRemoved) {
			analyticsEventsService.deletedATicket(ticketId, this.planId, action.type, isFloatingTicket, ticketType);
		}
	}

	private swimlaneEventHandling() {
		this.swimlanes.data.rawEvent$
			.pipe(
				takeUntil(this.internalShutdown),
				filter((a) => a.type === DefaultActionTypes.addItem)
			)
			.subscribe((a) => {
				const oldX = (this.scopeSoup.hud.x / this.scopeSoup.hud.z) * -1;
				const swimlaneNewBottom = (this.scopeSoup.hud.h - 135) / this.scopeSoup.hud.z; // -135 so new swimlane doesn't overlap with zoom controls

				// if new swimlane is offscreen
				if (
					a.payload.view.screenTop < 0 ||
					a.payload.view.screenBottom >
						Math.max(document.documentElement.clientHeight, window.innerHeight || 0)
				) {
					this.scopeSoup.hud.focusAtPoint(
						oldX,
						a.payload.view.bottom - swimlaneNewBottom,
						this.scopeSoup.hud.z
					);
				}
			});
	}

	private testExceptionWrite(ticketId: string, exceptionDays: { [key: string]: unknown }) {
		const ticketWorkDaysPath = fancyFirebaseGetters.getTicketWorkDaysPath(
			this.projectId,
			this.planId,
			ticketId
		);
		const exceptionsRef = fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(
			ticketWorkDaysPath,
			this.projectId
		);

		for (const day in exceptionDays) {
			exceptionDays[day] = {
				work: exceptionDays[day],
			};
		}

		exceptionsRef.set(exceptionDays);
	}

	// TEMP STUFF: all of functions bellow should be migrated to some kind of project info service once that is created.
	// It's currently legacy angularjs right now.
	// Also likely to be rewritten to not write to firebase directly.
	public fetchAllowedLinks() {
		this.project.object$
			.pipe(
				filter((project) => !!project),
				map((project) => {
					if (project && project.allowedLinks) {
						return project.allowedLinks;
					} else {
						this.logger.log('Allowed links list does not exist.');
						return null;
					}
				}),
				takeUntil(this.internalShutdown)
			)
			.subscribe(this.allowedProjectLinks$);
	}
	// TEMP: this logic likely will need to move or changed later as well
	public saveAllowedLinksString(linksStr: string) {
		const linkStrVal = extractHostName(linksStr);
		const projectPath = fancyFirebaseGetters.getProjectsPath(this.projectId);
		const allowedLinksRef = fancyFirebaseReferencer
			.getLegacyRef<firebase.database.Reference>(projectPath, this.projectId)
			.child('allowedLinks');

		if (linksStr) {
			allowedLinksRef.set(linkStrVal).catch((err) => this.logger.log(err));
			allowedLinksRef.off();
		} else {
			allowedLinksRef.remove().catch((err) => this.logger.log(err));
			allowedLinksRef.off();
		}
	}
}

class PlanStateLifeCycle {
	private angular = false;
	private plan_controller = false;
	private canvas = false;
	private everything = false;

	private map = {
		angular: StateEvents.INITIALIZED,
		plan_controller: StateEvents.SCOPE_SOUPED,
		canvas: StateEvents.CANVAS_LOADED,
	};

	public angularLoaded(): StateEvents {
		return this.calcState('angular');
	}
	public scopeSoupLoaded(): StateEvents {
		return this.calcState('plan_controller');
	}
	public canvasLoaded(): StateEvents {
		return this.calcState('canvas');
	}

	private calcState(key: 'angular' | 'plan_controller' | 'canvas'): StateEvents {
		if (this[key]) {
			return StateEvents.INVALID;
		} //no returning
		window.perf.log('planState - ' + key, { previousEventName: 'planState - angular' });
		this[key] = true;
		if (this.angular && this.plan_controller && this.canvas) {
			return StateEvents.FULLY_LOADED;
		} else {
			return this.map[key];
		}
	}
}
